import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MainRoutes } from "./routes";
import { useLocation } from "react-router";
import { AuthenticationContext, ExperienceContext, MemberEligibilityContext, MultifactorAuthenticationContext } from "./context";
import { authnzRoutes } from "./routes/routes";
import { useGetUserPreferencesQuery } from "./api/preferences";
import { useTranslation } from "react-i18next";
import { LanguageModal } from "./pages/Dashboard/LanguageModal";
import { AuthnzWrapper } from "./components";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Spinner } from "capitalrx-components-2.0/lib/general";
import { resetLanguageToBrowserSettings } from "./utils/language";

const App = (): JSX.Element => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const { authState, refreshToken } = useContext(AuthenticationContext);
  const { isLoadingEligibility } = useContext(MemberEligibilityContext);
  const { isLoadingExperience } = useContext(ExperienceContext);
  const { isLoadingMultifactorAuth, isOnMfaSetupPage } = useContext(MultifactorAuthenticationContext);
  const { data: userPreferences, isLoading: isLoadingUserPreferences } = useGetUserPreferencesQuery();
  const { i18n } = useTranslation();
  const [didConsiderLanguagePreference, setDidConsiderLanguagePreference] = useState<boolean>(false);
  const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);

  const whitelistRoutes = useMemo(() => {
    return authnzRoutes.map((r) => r.path);
  }, []);

  useEffect(() => {
    function reset(): void {
      if (timeoutRef.current === null) return;

      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    function queue(): void {
      timeoutRef.current = setTimeout(() => {
        refresh();
      }, 5000);
    }

    async function refresh(): Promise<void> {
      reset();
      const currentRouteInWhitelist = whitelistRoutes.some((r) => location.pathname.endsWith(r));
      if (!currentRouteInWhitelist) {
        await refreshToken();
      }
      queue();
    }

    queue();
    return reset;
  }, [location]);

  /**
   * Make this a memoized value to avoid the stuttering on re-renders of
   * the contexts that provide these values.
   */
  const shouldRenderLoading = useMemo(() => {
    if (authState === "signedIn") {
      return isLoadingEligibility || isLoadingExperience || isLoadingMultifactorAuth;
    } else {
      return false;
    }
  }, [isLoadingEligibility, isLoadingExperience, isLoadingMultifactorAuth, authState]);

  useEffect(() => {
    if (authState === "loading") {
      // when we're loading the app, use the browser language
      resetLanguageToBrowserSettings(i18n);
      return;
    } else if (authState === "signedOut") {
      // when signed out, forget about language preferences and use the browser language
      window.localStorage.removeItem("userConfirmedLanguageChoice");
      setDidConsiderLanguagePreference(false);
      resetLanguageToBrowserSettings(i18n);
      return;
    }

    // never show language modal if...
    if (
      didConsiderLanguagePreference // it was already shown before
      || shouldRenderLoading // we're still loading the base app
      || authState !== "signedIn" // we're not signed in
      || isOnMfaSetupPage // we're forcing the user to choose their MFA method
      || location.pathname === "/login" // we're on the login page
      || isLoadingUserPreferences || userPreferences == null // user preferences aren't loaded
      || window.localStorage.getItem("userConfirmedLanguageChoice") === "true" // we already bothered the user
    ) {
      return;
    }

    // if we're emulating, don't open the language modal, not even if we open a new tab; use the browser language
    if (window.sessionStorage.getItem("isEmulation")) {
      window.localStorage.setItem("userConfirmedLanguageChoice", "true");
      setDidConsiderLanguagePreference(true);
      resetLanguageToBrowserSettings(i18n);
      return;
    }

    // we can consider the user's language preference now...
    if (!!userPreferences.language && userPreferences.language !== i18n.language) {
      i18n.changeLanguage(userPreferences.language);
      window.localStorage.setItem("userConfirmedLanguageChoice", "true");
    } else if (!userPreferences.language) {
      setShowLanguageModal(true);
    }
    setDidConsiderLanguagePreference(true);
  }, [authState, didConsiderLanguagePreference, shouldRenderLoading, isOnMfaSetupPage, isLoadingUserPreferences, userPreferences, i18n, location.pathname]);

  const onLanguageFormSubmit = () => {
    window.localStorage.setItem("userConfirmedLanguageChoice", "true");
    setShowLanguageModal(false);
  };

  return (
    <div className="app-wrapper ">
      {shouldRenderLoading && (
        <AuthnzWrapper bypassLoginRequired pageHeader="Fetching Your Information" pageTitle="Loading">
          <Shelf>
            <Shelf.Spacer />
            <Spinner size="large" />
            <Shelf.Spacer />
          </Shelf>
        </AuthnzWrapper>
      )}
      {!shouldRenderLoading && (
        <>
          <LanguageModal visible={showLanguageModal} onSubmit={onLanguageFormSubmit}/>
          <MainRoutes />
        </>
      )}
    </div>
  );
};

export default App;
